import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  FiChevronDown,
  FiHeart,
  FiSearch,
  FiShoppingBag,
  FiSliders,
  FiX,
} from "react-icons/fi";
import "../css/shop.scss";
import productData, { productCategories } from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import { CartContext } from "./CartContext";

const sortOptions = [
  { value: "featured", label: "Featured" },
  { value: "best-selling", label: "Best selling" },
  { value: "title-ascending", label: "Alphabetically, A–Z" },
  { value: "title-descending", label: "Alphabetically, Z–A" },
  { value: "price-ascending", label: "Price, low to high" },
  { value: "price-descending", label: "Price, high to low" },
  { value: "created-ascending", label: "Date, old to new" },
  { value: "newest", label: "Date, new to old" },
];

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const getProductVariant = (product) => {
  const variants = Array.isArray(product.size) ? product.size : [product.size];
  return variants.find(Boolean) || "Standard";
};

const getProductColor = (product) => {
  const colors = Array.isArray(product.colors) ? product.colors : [product.colors];
  return colors.find(Boolean) || "Default";
};

function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToCart, isWishlisted, toggleWishlist } = useContext(CartContext);
  const [filterOpen, setFilterOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [addedProductId, setAddedProductId] = useState(null);

  const queryKey = searchParams.toString();
  const query = searchParams.get("q") || "";
  const selectedCategory = searchParams.get("category") || "All";
  const availability = searchParams.get("availability") || "all";
  const activeSort = sortOptions.some(
    (option) => option.value === searchParams.get("sort")
  )
    ? searchParams.get("sort")
    : "featured";
  const wishlistView = searchParams.get("view") === "wishlist";
  const minimumPrice = Math.max(0, Number(searchParams.get("min")) || 0);
  const maximumPriceParam = Number(searchParams.get("max"));

  const catalog = useMemo(() => {
    if (!Array.isArray(productData)) {
      return { products: [], error: "The product catalog could not be loaded." };
    }

    const products = productData.filter(
      (product) =>
        product &&
        Number.isFinite(Number(product.id)) &&
        product.name &&
        Number.isFinite(Number(product.price))
    );

    if (!products.length && productData.length) {
      return { products: [], error: "The product catalog is unavailable." };
    }

    return { products, error: null };
  }, []);

  const catalogMaximum = useMemo(
    () =>
      Math.ceil(
        catalog.products.reduce(
          (highest, product) => Math.max(highest, Number(product.price)),
          0
        )
      ),
    [catalog.products]
  );
  const maximumPrice =
    Number.isFinite(maximumPriceParam) && maximumPriceParam > 0
      ? maximumPriceParam
      : catalogMaximum;

  const [searchInput, setSearchInput] = useState(query);
  const [minimumInput, setMinimumInput] = useState(
    minimumPrice ? String(minimumPrice) : ""
  );
  const [maximumInput, setMaximumInput] = useState(
    searchParams.has("max") ? String(maximumPrice) : ""
  );

  const updateParameters = useCallback(
    (updates, options = {}) => {
      const next = new URLSearchParams(searchParams);

      Object.entries(updates).forEach(([key, value]) => {
        const shouldDelete =
          value === undefined ||
          value === null ||
          value === "" ||
          value === "all" ||
          (key === "category" && value === "All") ||
          (key === "sort" && value === "featured");

        if (shouldDelete) next.delete(key);
        else next.set(key, String(value));
      });

      setSearchParams(next, { replace: Boolean(options.replace) });
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    setSearchInput(query);
    setMinimumInput(minimumPrice ? String(minimumPrice) : "");
    setMaximumInput(searchParams.has("max") ? String(maximumPrice) : "");
  }, [maximumPrice, minimumPrice, query, queryKey, searchParams]);

  useEffect(() => {
    setIsLoading(true);
    const timer = window.setTimeout(() => setIsLoading(false), 260);
    return () => window.clearTimeout(timer);
  }, [queryKey]);

  useEffect(() => {
    document.title = `${wishlistView ? "Wishlist" : "Shop"} — ${
      siteConfig.wordmark
    }`;
  }, [wishlistView]);

  useEffect(() => {
    document.body.classList.toggle("shop-filter-open", filterOpen);

    const closeOnEscape = (event) => {
      if (event.key === "Escape") setFilterOpen(false);
    };

    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.classList.remove("shop-filter-open");
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [filterOpen]);

  const categoryCounts = useMemo(
    () =>
      productCategories.reduce((counts, category) => {
        counts[category] =
          category === "All"
            ? catalog.products.length
            : catalog.products.filter((product) => product.category === category)
                .length;
        return counts;
      }, {}),
    [catalog.products]
  );

  const visibleProducts = useMemo(() => {
    const normalizedQuery = normalizeText(query);
    const filtered = catalog.products.filter((product) => {
      const searchableContent = normalizeText(
        [
          product.name,
          product.category,
          product.collection,
          product.description,
          ...(product.tags || []),
        ].join(" ")
      );
      const matchesSearch =
        !normalizedQuery || searchableContent.includes(normalizedQuery);
      const matchesCategory =
        selectedCategory === "All" || product.category === selectedCategory;
      const productInventory = Number(product.inventory) || 0;
      const matchesAvailability =
        availability === "all" ||
        (availability === "in-stock" && productInventory > 0) ||
        (availability === "out-of-stock" && productInventory <= 0);
      const productPrice = Number(product.price);
      const matchesPrice =
        productPrice >= minimumPrice && productPrice <= maximumPrice;
      const matchesWishlist = !wishlistView || isWishlisted(product.id);

      return (
        matchesSearch &&
        matchesCategory &&
        matchesAvailability &&
        matchesPrice &&
        matchesWishlist
      );
    });

    return [...filtered].sort((first, second) => {
      switch (activeSort) {
        case "best-selling":
          return Number(second.reviews || 0) - Number(first.reviews || 0);
        case "title-ascending":
          return first.name.localeCompare(second.name);
        case "title-descending":
          return second.name.localeCompare(first.name);
        case "price-ascending":
          return Number(first.price) - Number(second.price);
        case "price-descending":
          return Number(second.price) - Number(first.price);
        case "created-ascending":
          return Number(first.id) - Number(second.id);
        case "newest":
          return Number(second.id) - Number(first.id);
        default:
          return (
            Number(Boolean(second.featured)) - Number(Boolean(first.featured)) ||
            Number(second.rating || 0) - Number(first.rating || 0) ||
            Number(second.reviews || 0) - Number(first.reviews || 0)
          );
      }
    });
  }, [
    activeSort,
    availability,
    catalog.products,
    isWishlisted,
    maximumPrice,
    minimumPrice,
    query,
    selectedCategory,
    wishlistView,
  ]);

  const activeFilterCount = [
    Boolean(query),
    selectedCategory !== "All",
    availability !== "all",
    minimumPrice > 0,
    searchParams.has("max"),
    wishlistView,
  ].filter(Boolean).length;

  const submitSearch = (event) => {
    event.preventDefault();
    updateParameters({ q: searchInput.trim() });
  };

  const applyPrice = (event) => {
    event.preventDefault();
    const nextMinimum = Math.max(0, Number(minimumInput) || 0);
    const rawMaximum = Number(maximumInput);
    const nextMaximum = maximumInput
      ? Math.min(catalogMaximum, Math.max(nextMinimum, rawMaximum || catalogMaximum))
      : "";

    setMinimumInput(nextMinimum ? String(nextMinimum) : "");
    setMaximumInput(nextMaximum ? String(nextMaximum) : "");
    updateParameters({ min: nextMinimum || "", max: nextMaximum });
  };

  const clearFilters = () => {
    const next = new URLSearchParams();
    if (activeSort !== "featured") next.set("sort", activeSort);
    setSearchInput("");
    setMinimumInput("");
    setMaximumInput("");
    setSearchParams(next);
  };

  const quickAdd = (product) => {
    addToCart(
      product.id,
      1,
      getProductVariant(product),
      getProductColor(product)
    );
    window.dispatchEvent(new CustomEvent("hushcider:open-cart"));
    setAddedProductId(product.id);
    window.setTimeout(() => {
      setAddedProductId((currentId) =>
        Number(currentId) === Number(product.id) ? null : currentId
      );
    }, 1500);
  };

  const filterContent = (
    <>
      <div className="shop-filter__mobile-head">
        <div>
          <span>Refine collection</span>
          <strong>FILTERS</strong>
        </div>
        <button
          type="button"
          aria-label="Close filters"
          onClick={() => setFilterOpen(false)}
        >
          <FiX />
        </button>
      </div>

      <div className="shop-filter__heading">
        <span>FILTER BY</span>
        {activeFilterCount > 0 && (
          <button type="button" onClick={clearFilters}>
            Clear all
          </button>
        )}
      </div>

      <fieldset className="shop-filter__group">
        <legend>Category</legend>
        <div className="shop-filter__options">
          {productCategories.map((category) => (
            <label key={category}>
              <input
                type="radio"
                name="shop-category"
                value={category}
                checked={selectedCategory === category}
                onChange={() => updateParameters({ category })}
              />
              <span className="shop-filter__control" aria-hidden="true" />
              <span>{category}</span>
              <small>{categoryCounts[category] || 0}</small>
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="shop-filter__group">
        <legend>Availability</legend>
        <div className="shop-filter__options">
          <label>
            <input
              type="radio"
              name="shop-availability"
              checked={availability === "all"}
              onChange={() => updateParameters({ availability: "all" })}
            />
            <span className="shop-filter__control" aria-hidden="true" />
            <span>All items</span>
          </label>
          <label>
            <input
              type="radio"
              name="shop-availability"
              checked={availability === "in-stock"}
              onChange={() => updateParameters({ availability: "in-stock" })}
            />
            <span className="shop-filter__control" aria-hidden="true" />
            <span>In stock</span>
          </label>
          <label>
            <input
              type="radio"
              name="shop-availability"
              checked={availability === "out-of-stock"}
              onChange={() => updateParameters({ availability: "out-of-stock" })}
            />
            <span className="shop-filter__control" aria-hidden="true" />
            <span>Sold out</span>
          </label>
        </div>
      </fieldset>

      <fieldset className="shop-filter__group shop-filter__group--price">
        <legend>Price</legend>
        <p>Highest price is {formatPrice(catalogMaximum)}</p>
        <form onSubmit={applyPrice}>
          <label>
            <span>$</span>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              max={catalogMaximum}
              placeholder="From"
              aria-label="Minimum price"
              value={minimumInput}
              onChange={(event) => setMinimumInput(event.target.value)}
            />
          </label>
          <span>—</span>
          <label>
            <span>$</span>
            <input
              type="number"
              inputMode="decimal"
              min="0"
              max={catalogMaximum}
              placeholder="To"
              aria-label="Maximum price"
              value={maximumInput}
              onChange={(event) => setMaximumInput(event.target.value)}
            />
          </label>
          <button type="submit">APPLY</button>
        </form>
      </fieldset>

      <button
        type="button"
        className="shop-filter__mobile-apply"
        onClick={() => setFilterOpen(false)}
      >
        SHOW {visibleProducts.length} RESULTS
      </button>
    </>
  );

  return (
    <div className="shop main">
      <main>
        <div className="shop__breadcrumb site-container" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <span>/</span>
          <span>{wishlistView ? "Wishlist" : "Shop all"}</span>
        </div>

        <section className="shop-hero site-container">
          <picture>
            <source media="(max-width: 680px)" srcSet="/img/story-studio-mobile.webp" />
            <img
              src="/img/story-studio.webp"
              alt={`${siteConfig.brandName} performance gaming studio`}
            />
          </picture>
          <div className="shop-hero__shade" aria-hidden="true" />
          <div className="shop-hero__copy">
            <p className="eyebrow">
              {wishlistView ? "YOUR EDIT" : "PERFORMANCE / DESIGN / CULTURE"}
            </p>
            <h1>{wishlistView ? "SAVED GEAR" : "THE COLLECTION"}</h1>
            <p>
              {wishlistView
                ? "Your shortlist, held in one place. Build the setup when you are ready."
                : "Purpose-built hardware and graphic objects made to bring a higher standard to every setup."}
            </p>
          </div>
          <span className="shop-hero__index" aria-hidden="true">
            {wishlistView ? "EDIT / 01" : "COLLECTION / 01"}
          </span>
        </section>

        <section className="shop-catalog site-container" aria-labelledby="collection-title">
          <div className="shop-catalog__intro">
            <div>
              <span className="eyebrow">{wishlistView ? "CURATED BY YOU" : "SHOP ALL"}</span>
              <h2 id="collection-title">
                {wishlistView ? "YOUR WISHLIST" : "GEAR FOR THE CLIMB"}
              </h2>
            </div>
            <p>
              {wishlistView
                ? "Save, compare, and come back to the pieces that fit your desk."
                : siteConfig.description}
            </p>
          </div>

          <div className="shop-toolbar">
            <form className="shop-search" role="search" onSubmit={submitSearch}>
              <FiSearch aria-hidden="true" />
              <input
                type="search"
                placeholder="SEARCH PRODUCTS"
                aria-label="Search products"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
              />
              {searchInput && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => {
                    setSearchInput("");
                    updateParameters({ q: "" });
                  }}
                >
                  <FiX />
                </button>
              )}
              <button type="submit">SEARCH</button>
            </form>

            <button
              type="button"
              className="shop-toolbar__filter"
              aria-controls="collection-filters"
              aria-expanded={filterOpen}
              onClick={() => setFilterOpen(true)}
            >
              <FiSliders />
              FILTER
              {activeFilterCount > 0 && <span>{activeFilterCount}</span>}
            </button>

            <div className="shop-toolbar__result" aria-live="polite">
              <span>{isLoading ? "UPDATING" : `${visibleProducts.length} RESULTS`}</span>
              {activeFilterCount > 0 && (
                <button type="button" onClick={clearFilters}>
                  CLEAR
                </button>
              )}
            </div>

            <label className="shop-sort">
              <span>SORT BY</span>
              <select
                value={activeSort}
                onChange={(event) => updateParameters({ sort: event.target.value })}
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <FiChevronDown aria-hidden="true" />
            </label>
          </div>

          <div className="shop-catalog__body">
            <div
              className={`shop-filter__backdrop ${filterOpen ? "is-open" : ""}`}
              onClick={() => setFilterOpen(false)}
              aria-hidden="true"
            />
            <aside
              id="collection-filters"
              className={`shop-filter ${filterOpen ? "is-open" : ""}`}
              aria-label="Product filters"
            >
              {filterContent}
            </aside>

            <div className="shop-results">
              {catalog.error ? (
                <div className="shop-state shop-state--error" role="alert">
                  <span>CATALOG ERROR</span>
                  <h3>WE LOST THE SIGNAL.</h3>
                  <p>{catalog.error} Refresh the page to try again.</p>
                  <button type="button" onClick={() => window.location.reload()}>
                    RELOAD COLLECTION
                  </button>
                </div>
              ) : isLoading ? (
                <div className="shop-grid" aria-label="Loading products" aria-busy="true">
                  {Array.from({ length: 8 }, (_, index) => (
                    <div className="shop-product-skeleton" key={index}>
                      <span />
                      <i />
                      <i />
                      <i />
                    </div>
                  ))}
                </div>
              ) : visibleProducts.length ? (
                <div className="shop-grid">
                  {visibleProducts.map((product, index) => {
                    const discount = product.compareAtPrice
                      ? Math.round(
                          (1 - Number(product.price) / Number(product.compareAtPrice)) *
                            100
                        )
                      : 0;
                    const wishlisted = isWishlisted(product.id);
                    const soldOut = Number(product.inventory) <= 0;
                    const primaryImage = product.images?.[0] || product.image;
                    const secondaryImage = product.images?.[1] || primaryImage;

                    return (
                      <article className="shop-product" key={product.id}>
                        <div className="shop-product__media">
                          <Link
                            to={`/shopDetails/${product.id}`}
                            aria-label={`View ${product.name}`}
                          >
                            <img
                              className="shop-product__image shop-product__image--primary"
                              src={primaryImage}
                              alt={product.name}
                              loading={index < 4 ? "eager" : "lazy"}
                            />
                            <img
                              className="shop-product__image shop-product__image--secondary"
                              src={secondaryImage}
                              alt=""
                              loading="lazy"
                              aria-hidden="true"
                            />
                          </Link>

                          <div className="shop-product__badges">
                            {soldOut ? (
                              <span>SOLD OUT</span>
                            ) : (
                              <>
                                {product.newArrival && <span>NEW</span>}
                                {(product.badge || discount > 0) && (
                                  <span className={discount > 0 ? "is-sale" : ""}>
                                    {product.badge || `${discount}% OFF`}
                                  </span>
                                )}
                              </>
                            )}
                          </div>

                          <button
                            type="button"
                            className={`shop-product__heart ${wishlisted ? "is-active" : ""}`}
                            aria-label={
                              wishlisted
                                ? `Remove ${product.name} from wishlist`
                                : `Add ${product.name} to wishlist`
                            }
                            aria-pressed={wishlisted}
                            onClick={() => toggleWishlist(product.id)}
                          >
                            <FiHeart />
                          </button>

                          <button
                            type="button"
                            className={`shop-product__quick-add ${
                              Number(addedProductId) === Number(product.id)
                                ? "is-added"
                                : ""
                            }`}
                            disabled={soldOut}
                            onClick={() => quickAdd(product)}
                          >
                            <FiShoppingBag />
                            <span>
                              {soldOut
                                ? "SOLD OUT"
                                : Number(addedProductId) === Number(product.id)
                                  ? "ADDED TO BAG"
                                  : "QUICK ADD"}
                            </span>
                          </button>
                        </div>

                        <div className="shop-product__details">
                          <div className="shop-product__meta">
                            <span>{product.category}</span>
                            <span>{product.collection || "Core"}</span>
                          </div>
                          <Link to={`/shopDetails/${product.id}`}>
                            <h3>{product.name}</h3>
                          </Link>
                          <div className="shop-product__bottom">
                            <div className="shop-product__rating">
                              <span aria-hidden="true">★★★★★</span>
                              <small>
                                {Number(product.rating || 0).toFixed(1)} ({product.reviews || 0})
                              </small>
                            </div>
                            <div className="shop-product__price">
                              <strong>{formatPrice(product.price)}</strong>
                              {product.compareAtPrice && (
                                <del>{formatPrice(product.compareAtPrice)}</del>
                              )}
                            </div>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              ) : (
                <div className="shop-state">
                  <span>{wishlistView ? "YOUR EDIT IS EMPTY" : "NO MATCHES"}</span>
                  <h3>{wishlistView ? "SAVE YOUR FIRST PIECE." : "TRY A WIDER SEARCH."}</h3>
                  <p>
                    {wishlistView
                      ? "Tap the heart on any product to keep it here for later."
                      : "Change a category, adjust the price, or clear your filters to see the full collection."}
                  </p>
                  <button type="button" onClick={clearFilters}>
                    {wishlistView ? "SHOP ALL GEAR" : "CLEAR ALL FILTERS"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default Shop;
